import { useState, useEffect, useCallback, useRef } from 'react'
import { API_ENDPOINTS } from '../config/api'

console.log('🔧 API_ENDPOINTS loaded:', API_ENDPOINTS)

// Helper function to extract wallet address from different Crossmint wallet structures
const getWalletAddress = (wallet) => {
  if (!wallet) return null

  // Try different possible properties for Crossmint wallet address
  const possibleAddressProps = [
    'address',           // Standard property
    'walletAddress',     // Alternative property
    'publicKey',         // Solana wallets
    'account',           // Some wallet implementations
    'userAddress',       // User-specific address
    'smartWallet.address', // Smart wallet address
    'wallet.address',    // Nested wallet address
    'account.address',   // Nested account address
  ]

  for (const prop of possibleAddressProps) {
    if (prop.includes('.')) {
      // Handle nested properties like 'smartWallet.address'
      const parts = prop.split('.')
      let value = wallet
      for (const part of parts) {
        value = value?.[part]
        if (!value) break
      }
      if (value && typeof value === 'string' && value.startsWith('0x')) {
        console.log(`✅ Found wallet address via ${prop}:`, value)
        return value
      }
    } else {
      // Handle direct properties
      const value = wallet[prop]
      if (value && typeof value === 'string' && value.startsWith('0x')) {
        console.log(`✅ Found wallet address via ${prop}:`, value)
        return value
      }
    }
  }

  // If no standard properties found, log the wallet structure for debugging
  console.log('🔍 No address found, wallet structure:', {
    wallet: wallet,
    keys: Object.keys(wallet),
    type: typeof wallet,
    constructor: wallet.constructor?.name
  })

  return null
}

export function useEtherith(wallet) {
  const [isInitialized, setIsInitialized] = useState(false)
  const [user, setUser] = useState(null)
  const [memories, setMemories] = useState([])
  const [publicMemories, setPublicMemories] = useState([])
  const [connectionStatus, setConnectionStatus] = useState('Connecting...')
  const [syncStatus, setSyncStatus] = useState('synced')
  const [debugInfo, setDebugInfo] = useState('Initializing Etherith...')
  const [peerCount, setPeerCount] = useState(0)
  const [networkStatus, setNetworkStatus] = useState('connecting')
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Refs to store Yjs and other persistent objects
  const walletCheckTimeoutRef = useRef(null)
  const lastWalletAddressRef = useRef(null)
  const ydocRef = useRef(null) // User-specific document
  const publicYdocRef = useRef(null) // Shared public archive document
  const memoriesArrayRef = useRef(null)
  const profileDataRef = useRef(null)
  const archiveDataRef = useRef(null)
  const heliaRef = useRef(null)
  const fsRef = useRef(null)
  const providersRef = useRef([])
  const isInitializingRef = useRef(false)

  // Initialize Etherith core systems
  const initializeEtherith = useCallback(async () => {
    // Prevent multiple initializations
    if (isInitializingRef.current || isInitialized) {
      console.log('⚠️ Etherith already initializing or initialized')
      return
    }

    // Check if wallet is connected (allow basic initialization without wallet)
    const walletAddress = getWalletAddress(wallet)
    console.log('🔍 InitializeEtherith wallet check:', {
      wallet: wallet,
      hasWallet: !!wallet,
      walletKeys: wallet ? Object.keys(wallet) : null,
      address: walletAddress,
      isAuthenticated
    })
    
    if (!walletAddress) {
      console.log('⚠️ Wallet not connected, doing basic initialization', {
        wallet: wallet,
        hasWallet: !!wallet,
        walletKeys: wallet ? Object.keys(wallet) : null,
        address: walletAddress
      })
      setIsInitialized(true)
      isInitializingRef.current = false
      return
    }

    isInitializingRef.current = true
    console.log('🚀 Initializing Etherith Memory Vault...', { walletAddress: walletAddress })

    try {
      // Load dependencies dynamically
      const [Y, { WebrtcProvider }, { IndexeddbPersistence }, { createHelia }, { unixfs }] = await Promise.all([
        import('https://esm.sh/yjs@13.6.27?bundle&no-check&external=none'),
        import('https://esm.sh/y-webrtc@10.3.0?bundle&no-check&external=none&deps=yjs@13.6.27'),
        import('https://esm.sh/y-indexeddb@9.0.12?bundle&no-check&external=none&deps=yjs@13.6.27'),
        import('https://esm.sh/helia@4.2.6?bundle&external=none'),
        import('https://esm.sh/@helia/unixfs@3.0.7?bundle&external=none')
      ])

      console.log('📦 Dependencies loaded successfully')

      // Create user-specific Yjs document for private memories
      const userDocId = `etherith-user-${walletAddress.toLowerCase()}`
      const ydoc = new Y.Doc()
      ydocRef.current = ydoc
      
      console.log('👤 User-specific document ID:', userDocId)

      // Create shared Yjs document for public archive (shared by all users)
      const publicDocId = 'etherith-public-archive'
      const publicYdoc = new Y.Doc()
      publicYdocRef.current = publicYdoc
      
      console.log('🌐 Shared public archive document ID:', publicDocId)

      // Create arrays for different data types
      memoriesArrayRef.current = ydoc.getArray('memories') // User-specific private memories
      profileDataRef.current = ydoc.getMap('profile') // User-specific profile
      archiveDataRef.current = publicYdoc.getArray('publicArchive') // Shared public archive

      // Update debug info function
      const updateDebugInfo = () => {
        setDebugInfo(prev => ({
          ...prev,
          memoriesCount: memoriesArrayRef.current?.length || 0,
          publicMemoriesCount: archiveDataRef.current?.length || 0,
          networkStatus: networkStatus,
          peerCount: peerCount,
          connectionStatus: connectionStatus,
          syncStatus: syncStatus,
          userDocId: userDocId,
          publicDocId: publicDocId,
          walletAddress: wallet.address,
          timestamp: new Date().toISOString()
        }))
      }

      // Listen for changes to the public archive with extensive debugging
      archiveDataRef.current.observe((event) => {
        console.log('📡 Public archive array changed:', {
          event,
          roomName: publicDocId,
          changes: event.changes,
          target: event.target,
          currentLength: archiveDataRef.current.length,
          clientID: publicYdoc.clientID,
          timestamp: new Date().toISOString()
        })
        loadPublicArchive()
        
        // Update debug info when archive changes
        updateDebugInfo()
      })

      // Listen for public archive document updates with comprehensive debugging
      publicYdoc.on('update', (update, origin, doc, tr) => {
        console.log('📄 Public document updated:', {
          updateSize: update.length,
          origin: origin,
          clientID: doc.clientID,
          transaction: tr,
          roomName: publicDocId,
          archiveLength: archiveDataRef.current?.length || 0,
          timestamp: new Date().toISOString()
        })
        
        // Check if this update affects the public archive
        if (archiveDataRef.current && archiveDataRef.current.length > 0) {
          console.log('🔄 Public archive update detected, reloading...')
          loadPublicArchive()
        }
        
        updateDebugInfo()
      })

      // Additional document debugging events
      publicYdoc.on('beforeTransaction', (tr, doc) => {
        console.log('🔄 Public document before transaction:', {
          transaction: tr,
          clientID: doc.clientID,
          roomName: publicDocId,
          timestamp: new Date().toISOString()
        })
      })

      publicYdoc.on('afterTransaction', (tr, doc) => {
        console.log('🔄 Public document after transaction:', {
          transaction: tr,
          clientID: doc.clientID,
          roomName: publicDocId,
          archiveLength: archiveDataRef.current?.length || 0,
          timestamp: new Date().toISOString()
        })
      })

      // Setup offline persistence for user-specific data
      const userIndexeddbProvider = new IndexeddbPersistence(userDocId, ydoc)
      userIndexeddbProvider.on('synced', () => {
        console.log('💾 User data synchronized')
        setSyncStatus('synced')
        loadMemoriesFromStorage()
        updateDebugInfo()
      })
      providersRef.current.push(userIndexeddbProvider)

      // Setup offline persistence for shared public archive
      const publicIndexeddbProvider = new IndexeddbPersistence(publicDocId, publicYdoc)
      publicIndexeddbProvider.on('synced', () => {
        console.log('💾 Public archive synchronized')
        loadPublicArchive()
        updateDebugInfo()
      })
      providersRef.current.push(publicIndexeddbProvider)

      // Setup P2P synchronization for user-specific data with extensive debugging
      const userWebrtcProvider = new WebrtcProvider(userDocId, ydoc, {
        signaling: [
          'wss://signaling.yjs.dev',
          'wss://y-webrtc-signaling-eu.herokuapp.com'
        ],
        maxConns: 10,
        filterBcConns: true
      })
      
      console.log('🌐 User WebRTC Provider initialized:', {
        roomName: userDocId,
        signalingServers: userWebrtcProvider.signalingUrls,
        maxConns: userWebrtcProvider.maxConns,
        clientID: ydoc.clientID,
        docGc: ydoc.gc
      })

      // Setup P2P synchronization for shared public archive with extensive debugging
      const publicWebrtcProvider = new WebrtcProvider(publicDocId, publicYdoc, {
        signaling: [
          'wss://signaling.yjs.dev',
          'wss://y-webrtc-signaling-eu.herokuapp.com'
        ],
        maxConns: 20, // More connections for public archive
        filterBcConns: true,
        // Additional options for better cross-network connectivity
        peerOpts: {
          config: {
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' },
              { urls: 'stun:stun2.l.google.com:19302' },
              { urls: 'stun:stun3.l.google.com:19302' },
              { urls: 'stun:stun4.l.google.com:19302' }
            ]
          }
        }
      })
      
      console.log('🌐 Public WebRTC Provider initialized:', {
        roomName: publicDocId,
        signalingServers: publicWebrtcProvider.signalingUrls,
        maxConns: publicWebrtcProvider.maxConns,
        clientID: publicYdoc.clientID,
        docGc: publicYdoc.gc
      })

      // User-specific WebRTC event handlers with comprehensive debugging
      userWebrtcProvider.on('peers', (peers) => {
        const userPeerCount = Array.isArray(peers) ? peers.length : (peers.peers ? peers.peers.length : 0)
        console.log(`👤 User WebRTC peers changed:`, {
          peerCount: userPeerCount,
          roomName: userDocId,
          connected: userWebrtcProvider.connected,
          connecting: userWebrtcProvider.connecting,
          peers: peers,
          clientID: ydoc.clientID
        })
        updateDebugInfo()
      })

      userWebrtcProvider.on('status', (status) => {
        console.log(`📡 User WebRTC status:`, {
          status,
          roomName: userDocId,
          connected: userWebrtcProvider.connected,
          connecting: userWebrtcProvider.connecting,
          peersCount: userWebrtcProvider.peers?.size || 0
        })
        updateDebugInfo()
      })

      userWebrtcProvider.on('connection-error', (error) => {
        console.error(`❌ User WebRTC connection error:`, {
          error,
          roomName: userDocId,
          connected: userWebrtcProvider.connected,
          signalingUrls: userWebrtcProvider.signalingUrls
        })
        updateDebugInfo()
      })

      userWebrtcProvider.on('connection-close', (event) => {
        console.log(`🔌 User WebRTC connection closed:`, {
          event,
          roomName: userDocId,
          connected: userWebrtcProvider.connected
        })
        updateDebugInfo()
      })

      // Public archive WebRTC event handlers with comprehensive debugging
      publicWebrtcProvider.on('peers', (peers) => {
        const publicPeerCount = Array.isArray(peers) ? peers.length : (peers.peers ? peers.peers.length : 0)
        console.log(`🌐 Public WebRTC peers changed:`, {
          peerCount: publicPeerCount,
          roomName: publicDocId,
          connected: publicWebrtcProvider.connected,
          connecting: publicWebrtcProvider.connecting,
          peers: peers,
          clientID: publicYdoc.clientID,
          archiveMemoriesCount: archiveDataRef.current?.length || 0
        })
        setPeerCount(publicPeerCount)
        setConnectionStatus(`${publicPeerCount} peers`)
        setNetworkStatus(publicPeerCount > 0 ? 'connected' : 'searching')
        updateDebugInfo()
      })

      publicWebrtcProvider.on('status', (status) => {
        console.log('📡 Public WebRTC status:', {
          status,
          roomName: publicDocId,
          connected: publicWebrtcProvider.connected,
          connecting: publicWebrtcProvider.connecting,
          peersCount: publicWebrtcProvider.peers?.size || 0,
          archiveMemoriesCount: archiveDataRef.current?.length || 0
        })
        setNetworkStatus(status.connected ? 'connected' : 'disconnected')
        updateDebugInfo()
      })

      publicWebrtcProvider.on('connection-error', (error) => {
        console.error('❌ Public WebRTC connection error:', {
          error,
          roomName: publicDocId,
          connected: publicWebrtcProvider.connected,
          signalingUrls: publicWebrtcProvider.signalingUrls,
          archiveMemoriesCount: archiveDataRef.current?.length || 0
        })
        setNetworkStatus('error')
        updateDebugInfo()
      })

      publicWebrtcProvider.on('connection-close', () => {
        console.log('🔌 Public WebRTC connection closed:', {
          roomName: publicDocId,
          connected: publicWebrtcProvider.connected,
          archiveMemoriesCount: archiveDataRef.current?.length || 0
        })
        setNetworkStatus('disconnected')
        updateDebugInfo()
      })

      // Additional WebRTC debugging events
      publicWebrtcProvider.on('synced', (synced) => {
        console.log('🔄 Public WebRTC synced:', {
          synced,
          roomName: publicDocId,
          peersCount: publicWebrtcProvider.peers?.size || 0,
          archiveMemoriesCount: archiveDataRef.current?.length || 0
        })
        updateDebugInfo()
      })

      // Enhanced WebRTC debugging for cross-network connectivity
      publicWebrtcProvider.on('connection-open', (peer) => {
        console.log('🔗 Public WebRTC connection opened:', {
          peer: peer,
          roomName: publicDocId,
          totalPeers: publicWebrtcProvider.peers?.size || 0,
          clientID: publicYdoc.clientID,
          timestamp: new Date().toISOString()
        })
        updateDebugInfo()
      })

      publicWebrtcProvider.on('connection-close', (peer) => {
        console.log('🔌 Public WebRTC connection closed:', {
          peer: peer,
          roomName: publicDocId,
          totalPeers: publicWebrtcProvider.peers?.size || 0,
          clientID: publicYdoc.clientID,
          timestamp: new Date().toISOString()
        })
        updateDebugInfo()
      })

      // Monitor signaling server connections
      publicWebrtcProvider.on('signaling-error', (error) => {
        console.error('❌ Public WebRTC signaling error:', {
          error: error,
          roomName: publicDocId,
          signalingUrls: publicWebrtcProvider.signalingUrls,
          timestamp: new Date().toISOString()
        })
        updateDebugInfo()
      })

      // Monitor ICE connection state changes
      if (publicWebrtcProvider.peers) {
        publicWebrtcProvider.peers.forEach((peer, peerId) => {
          console.log('👥 Public WebRTC peer details:', {
            peerId: peerId,
            peer: peer,
            roomName: publicDocId,
            connectionState: peer.connectionState,
            iceConnectionState: peer.iceConnectionState
          })
        })
      }

      providersRef.current.push(userWebrtcProvider)
      providersRef.current.push(publicWebrtcProvider)

      // Periodic peer discovery check for cross-network connectivity
      const peerDiscoveryInterval = setInterval(() => {
        const publicPeers = publicWebrtcProvider.peers?.size || 0
        const userPeers = userWebrtcProvider.peers?.size || 0
        
        console.log('🔍 Periodic peer discovery check:', {
          publicPeers: publicPeers,
          userPeers: userPeers,
          publicConnected: publicWebrtcProvider.connected,
          userConnected: userWebrtcProvider.connected,
          roomName: publicDocId,
          timestamp: new Date().toISOString()
        })

        // If no peers after 30 seconds, try to reconnect
        if (publicPeers === 0 && publicWebrtcProvider.connected) {
          console.log('🔄 No public peers detected, attempting reconnection...')
          publicWebrtcProvider.disconnect()
          setTimeout(() => {
            publicWebrtcProvider.connect()
          }, 1000)
        }
      }, 10000) // Check every 10 seconds

      // Store interval for cleanup
      providersRef.current.push({ destroy: () => clearInterval(peerDiscoveryInterval) })

      // Initialize Helia IPFS node
      try {
        console.log('🌐 Initializing Helia IPFS node...')
        const helia = await createHelia({
          start: false,
          config: {
            Bootstrap: [
              '/dnsaddr/bootstrap.libp2p.io/p2p/QmNnooDu7bfjPFoTZYxMNLWUQJyrVwtbZg5gBMjTezGAJN',
              '/dnsaddr/bootstrap.libp2p.io/p2p/QmbLHAnMoJPWSCR5Zp6w6hV6qGMBq1ey7HbqFBLj7dkJC'
            ]
          }
        })

        await helia.start()
        heliaRef.current = helia
        fsRef.current = unixfs(helia)
        console.log('✅ Helia IPFS node ready')
      } catch (error) {
        console.warn('⚠️ Helia initialization failed:', error)
        console.log('📄 Will use Pinata-only mode')
      }

      // Check for existing authentication
      checkExistingAuth()

      // Load existing memories
      loadMemoriesFromStorage()

      setDebugInfo({
        status: 'Initialized',
        memoriesCount: memoriesArrayRef.current.length,
        publicMemoriesCount: archiveDataRef.current.length,
        authenticated: isAuthenticated,
        networkStatus: networkStatus,
        peerCount: peerCount,
        connectionStatus: connectionStatus,
        syncStatus: syncStatus,
        timestamp: new Date().toISOString()
      })

      setIsInitialized(true)
      console.log('✅ Etherith initialized successfully')

    } catch (error) {
      console.error('❌ Failed to initialize Etherith:', error)
      setDebugInfo(`Error: ${error.message}`)
      isInitializingRef.current = false
    }
  }, [isAuthenticated])

  // Check for existing authentication
  const checkExistingAuth = useCallback(() => {
    // If wallet is provided, use it for authentication
    if (wallet) {
      const walletAddress = getWalletAddress(wallet)
      
      if (walletAddress) {
        const userData = {
          id: walletAddress,
          address: walletAddress,
          username: `User_${walletAddress.slice(0, 6)}`,
          avatar: null,
          avatarURL: `https://api.dicebear.com/7.x/identicon/svg?seed=${walletAddress}`,
          timestamp: Date.now(),
          type: 'crossmint'
        }
        
        console.log('✅ Crossmint wallet connected:', userData.address)
        setUser(userData)
        setIsAuthenticated(true)
        updateProfileUI(userData)
        
        // Store in Yjs for persistence
        if (profileDataRef.current) {
          profileDataRef.current.set('user', userData)
          profileDataRef.current.set('lastLogin', Date.now())
        }
        return
      } else {
        console.log('⚠️ Wallet detected but no address found:', wallet)
      }
    }

    // Check Yjs storage for existing user (fallback)
    if (profileDataRef.current) {
      const storedUser = profileDataRef.current.get('user')
      if (storedUser) {
        console.log('👤 Found existing user session in Yjs')
        setUser(storedUser)
        setIsAuthenticated(true)
        updateProfileUI(storedUser)
      }
    }
  }, [wallet])

  // Additional effect to handle wallet changes and authentication
  useEffect(() => {
    if (wallet) {
      const walletAddress = getWalletAddress(wallet)
      if (walletAddress && !isAuthenticated) {
        console.log('🔄 Wallet detected, triggering authentication...')
        checkExistingAuth()
      }
    }
  }, [wallet, isAuthenticated, checkExistingAuth])

  // Ensure storage arrays are initialized for authenticated users
  useEffect(() => {
    if (isAuthenticated && wallet) {
      const walletAddress = getWalletAddress(wallet)
      if (walletAddress && (!memoriesArrayRef.current || !archiveDataRef.current)) {
        console.log('🔧 Storage arrays not initialized, triggering full initialization...')
        // Trigger full initialization to ensure storage arrays are created
        initializeEtherith()
      }
    }
  }, [isAuthenticated, wallet, initializeEtherith])

  // Create a simple storage initialization for immediate use
  const initializeStorageArrays = useCallback(() => {
    if (isAuthenticated && wallet) {
      const walletAddress = getWalletAddress(wallet)
      if (walletAddress) {
        console.log('🔧 Creating temporary storage arrays for immediate use...')
        
        // Create temporary Y.Doc instances for immediate use
        if (!ydocRef.current) {
          const Y = window.Y
          if (Y) {
            const userDocId = `etherith-user-${walletAddress.toLowerCase()}`
            const ydoc = new Y.Doc()
            ydocRef.current = ydoc
            memoriesArrayRef.current = ydoc.getArray('memories')
            profileDataRef.current = ydoc.getMap('profile')
            console.log('✅ User storage arrays created temporarily')
          }
        }
        
        if (!publicYdocRef.current) {
          const Y = window.Y
          if (Y) {
            const publicDocId = 'etherith-public-archive'
            const publicYdoc = new Y.Doc()
            publicYdocRef.current = publicYdoc
            archiveDataRef.current = publicYdoc.getArray('publicArchive')
            console.log('✅ Public storage arrays created temporarily')
          }
        }
      }
    }
  }, [isAuthenticated, wallet])

  // Update profile UI after login
  const updateProfileUI = useCallback((userData) => {
    setUser(userData)
    setIsAuthenticated(true)

    setDebugInfo({
      authenticated: true,
      user: {
        id: userData.id,
        username: userData.username,
        avatar: !!userData.avatar
      },
      timestamp: new Date().toISOString()
    })
  }, [])

  // Crossmint authentication is handled by the providers
  // No need for separate login/logout functions

  // Load memories from storage
  const loadMemoriesFromStorage = useCallback(() => {
    if (memoriesArrayRef.current) {
      const memoriesData = memoriesArrayRef.current.toArray()
      setMemories(memoriesData)
      console.log(`📊 Loaded ${memoriesData.length} memories`)
    }
  }, [])

  // Comprehensive debug information based on Yjs documentation
  const updateDebugInfo = useCallback(() => {
    const debugData = {
      // Basic state
      isInitialized,
      isAuthenticated,
      user: user ? { id: user.id, address: user.address, username: user.username } : null,
      
      // Document counts
      memoriesCount: memoriesArrayRef.current?.length || 0,
      publicMemoriesCount: archiveDataRef.current?.length || 0,
      
      // Document IDs
      userDocId: user ? `etherith-user-${user.address.toLowerCase()}` : null,
      publicDocId: 'etherith-public-archive',
      
      // Network status
      networkStatus: networkStatus,
      peerCount: peerCount,
      connectionStatus: connectionStatus,
      syncStatus: syncStatus,
      
      // Yjs document states
      userDocState: ydocRef.current ? {
        clientID: ydocRef.current.clientID,
        gc: ydocRef.current.gc,
        isDestroyed: ydocRef.current.isDestroyed || false
      } : null,
      
      publicDocState: publicYdocRef.current ? {
        clientID: publicYdocRef.current.clientID,
        gc: publicYdocRef.current.gc,
        isDestroyed: publicYdocRef.current.isDestroyed || false
      } : null,
      
      // Storage arrays state
      storageArrays: {
        memoriesArray: !!memoriesArrayRef.current,
        profileData: !!profileDataRef.current,
        archiveData: !!archiveDataRef.current
      },
      
      // Provider states with detailed detection
      providers: {
        userIndexedDB: (() => {
          const provider = providersRef.current.find(p => 
            (p.constructor.name === 'IndexeddbPersistence' || p.name === 'IndexeddbPersistence') && 
            (p.docName?.includes('user') || p.docName?.includes(user?.address?.toLowerCase()))
          )
          return provider ? 'active' : 'inactive'
        })(),
        publicIndexedDB: (() => {
          const provider = providersRef.current.find(p => 
            (p.constructor.name === 'IndexeddbPersistence' || p.name === 'IndexeddbPersistence') && 
            (p.docName?.includes('public') || p.docName === 'etherith-public-archive')
          )
          return provider ? 'active' : 'inactive'
        })(),
        userWebRTC: (() => {
          const provider = providersRef.current.find(p => 
            (p.constructor.name === 'WebrtcProvider' || p.name === 'WebrtcProvider') && 
            (p.roomName?.includes('user') || p.roomName?.includes(user?.address?.toLowerCase()))
          )
          return provider ? `active (${provider.peers?.size || 0} peers)` : 'inactive'
        })(),
        publicWebRTC: (() => {
          const provider = providersRef.current.find(p => 
            (p.constructor.name === 'WebrtcProvider' || p.name === 'WebrtcProvider') && 
            (p.roomName?.includes('public') || p.roomName === 'etherith-public-archive')
          )
          return provider ? `active (${provider.peers?.size || 0} peers)` : 'inactive'
        })(),
        totalProviders: providersRef.current.length,
        providerTypes: providersRef.current.map(p => ({
          type: p.constructor.name || p.name || 'unknown',
          docName: p.docName,
          roomName: p.roomName,
          connected: p.connected,
          synced: p.synced,
          peersCount: p.peers?.size || 0
        }))
      },
      
      // Awareness states (if available)
      awareness: {
        userAwareness: providersRef.current.find(p => p.awareness)?.awareness ? {
          clientID: providersRef.current.find(p => p.awareness)?.awareness.clientID,
          localState: providersRef.current.find(p => p.awareness)?.awareness.getLocalState(),
          states: Array.from(providersRef.current.find(p => p.awareness)?.awareness.getStates()?.entries() || [])
        } : null
      },
      
      // WebRTC connection details with enhanced debugging
      webrtcDetails: {
        signalingServers: [
          'wss://signaling.yjs.dev',
          'wss://y-webrtc-signaling-eu.herokuapp.com'
        ],
        connectionStates: providersRef.current
          .filter(p => p.constructor.name === 'WebrtcProvider' || p.name === 'WebrtcProvider')
          .map(p => ({
            roomName: p.roomName,
            connected: p.connected,
            connecting: p.connecting,
            peersCount: p.peers?.size || 0,
            signalingUrls: p.signalingUrls || [],
            awareness: p.awareness ? 'enabled' : 'disabled',
            bcConns: p.bcConns,
            maxConns: p.maxConns,
            filterBcConns: p.filterBcConns,
            peerDetails: p.peers ? Array.from(p.peers.entries()).map(([peerId, peer]) => ({
              peerId: peerId.slice(0, 8) + '...',
              connectionState: peer.connectionState,
              iceConnectionState: peer.iceConnectionState,
              iceGatheringState: peer.iceGatheringState,
              signalingState: peer.signalingState
            })) : []
          })),
        totalWebRTCProviders: providersRef.current.filter(p => 
          p.constructor.name === 'WebrtcProvider' || p.name === 'WebrtcProvider'
        ).length
      },
      
      // IndexedDB details
      indexedDBDetails: {
        userDoc: providersRef.current
          .filter(p => p.constructor.name === 'IndexeddbPersistence' && p.docName?.includes('user'))
          .map(p => ({
            docName: p.docName,
            synced: p.synced
          })),
        publicDoc: providersRef.current
          .filter(p => p.constructor.name === 'IndexeddbPersistence' && p.docName?.includes('public'))
          .map(p => ({
            docName: p.docName,
            synced: p.synced
          }))
      },
      
      timestamp: new Date().toISOString()
    }
    
    setDebugInfo(debugData)
  }, [isInitialized, isAuthenticated, user, networkStatus, peerCount, connectionStatus, syncStatus])

  // Load public archive
  const loadPublicArchive = useCallback(() => {
    if (archiveDataRef.current) {
      const archiveMemories = archiveDataRef.current.toArray()
      setPublicMemories(archiveMemories)
      console.log(`🌐 Loaded ${archiveMemories.length} public memories`)
      console.log('📋 Public archive contents:', archiveMemories.map(m => ({ 
        id: m.id, 
        status: m.status, 
        visibility: m.visibility, 
        preservationMode: m.preservationMode,
        timestamp: new Date(m.timestamp).toISOString()
      })))
      
      // Log detailed sync information
      console.log('🔄 Archive sync status:', {
        totalMemories: archiveMemories.length,
        publicMemories: archiveMemories.filter(m => m.visibility === 'public').length,
        moderatedMemories: archiveMemories.filter(m => m.status === 'moderated').length,
        pinnedMemories: archiveMemories.filter(m => m.pinned).length,
        networkStatus: networkStatus,
        peerCount: peerCount
      })
      
      updateDebugInfo()
    }
  }, [updateDebugInfo, networkStatus, peerCount])

  // Search archive
  const searchArchive = useCallback((searchTerm) => {
    if (!archiveDataRef.current) return

    const allMemories = archiveDataRef.current.toArray()

    if (!searchTerm) {
      setPublicMemories(allMemories)
      return
    }

    const filtered = allMemories.filter(memory => {
      return (
        memory.note.toLowerCase().includes(searchTerm.toLowerCase()) ||
        memory.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        memory.type.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })

    setPublicMemories(filtered)
    console.log(`🔍 Search results: ${filtered.length} memories found`)
  }, [])

  // Upload memory
  const uploadMemory = useCallback(async (files, note, preservationMode = 'private') => {
    console.log('📤 Starting memory upload...', { preservationMode })

    if (!files.length || !note.trim()) {
      throw new Error('Please select files and add a memory note.')
    }

    // Check if wallet is connected and Yjs arrays are initialized
    if (!isAuthenticated) {
      throw new Error('Please connect your wallet first to upload memories.')
    }

    // Ensure storage arrays are initialized
    if (!memoriesArrayRef.current || !archiveDataRef.current) {
      console.log('🔧 Storage arrays not ready, initializing...')
      initializeStorageArrays()
      
      // Wait a moment for initialization
      await new Promise(resolve => setTimeout(resolve, 100))
      
      if (!memoriesArrayRef.current || !archiveDataRef.current) {
        throw new Error('Storage initialization in progress. Please try again in a moment.')
      }
    }

    try {
      setSyncStatus('uploading')

      for (const file of files) {
        console.log(`📁 Processing file: ${file.name}`)

        // Create memory object based on preservation mode
        const memory = {
          id: `mem_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          note: note.trim(),
          type: getFileType(file),
          preservationMode: preservationMode,
          visibility: preservationMode === 'public' ? 'public' : 'private', // For backward compatibility
          timestamp: Date.now(),
          userId: wallet?.address || user?.id || 'anonymous',
          fileName: file.name,
          fileSize: file.size,
          status: 'uploading',
          cid: null,
          pinned: false
        }

        // Process based on preservation mode
        try {
          let updatedMemory = { ...memory }

          if (preservationMode === 'private') {
            // Private mode: Local only, IndexedDB storage
            console.log('🔒 Processing private memory (local only)...')
            updatedMemory.status = 'stored'
            updatedMemory.storageType = 'local'
            updatedMemory.cid = null
            updatedMemory.pinned = false
            
            // Store in IndexedDB only (already added to Yjs array above)
            console.log('✅ Private memory stored locally')
            
          } else if (preservationMode === 'public') {
            // Public mode: Yjs shared document + IPFS
            console.log('🌐 Processing public memory (Yjs + IPFS)...')

            // Add to Yjs array immediately (shows as "uploading")
            if (memoriesArrayRef.current) {
              memoriesArrayRef.current.push([memory])
              loadMemoriesFromStorage()
            }

            // Generate CID with Helia (if available)
            let localCid = null
            if (fsRef.current) {
              console.log('🔗 Generating CID with Helia...')
              const fileBytes = new Uint8Array(await file.arrayBuffer())
              localCid = await fsRef.current.addBytes(fileBytes)
              console.log('✅ Local CID generated:', localCid.toString())
            }

            // Upload to Pinata for permanent storage
            console.log('☁️ Uploading to Pinata...')
            const uploadResult = await uploadToPinata(file, memory)

            if (uploadResult.success) {
              updatedMemory = {
                ...memory,
                status: uploadResult.proof.pinned ? 'pinned' : 'uploaded',
                cid: uploadResult.cid,
                pinned: uploadResult.proof.pinned,
                proof: uploadResult.proof,
                localCid: localCid ? localCid.toString() : null,
                storageType: 'ipfs'
              }

              // AI Moderation for public memories (optional)
              console.log('🛡️ Moderating public memory...')
              try {
                const moderationResult = await moderateContent({
                  memoryNote: memory.note,
                  fileName: memory.fileName,
                  fileType: memory.type,
                  cid: uploadResult.cid
                })

                updatedMemory.moderation = moderationResult.moderation

                if (moderationResult.moderation.approved) {
                  updatedMemory.status = 'moderated'
                  console.log('✅ Memory approved by moderation')
                } else {
                  updatedMemory.status = 'rejected'
                  console.log('❌ Memory rejected by moderation:', moderationResult.moderation.reason)
                }
              } catch (moderationError) {
                console.warn('⚠️ Moderation failed, proceeding without moderation:', moderationError)
                updatedMemory.moderationError = moderationError.message
                updatedMemory.status = 'uploaded' // Keep as uploaded if moderation fails
              }

              // Always add public memories to archive (moderation is optional)
              if (archiveDataRef.current) {
                archiveDataRef.current.push([updatedMemory])
                console.log('✅ Memory added to public archive')

                // Reload public archive to update UI
                loadPublicArchive()
              }
            } else {
              throw new Error(uploadResult.error || 'Upload failed')
            }
            
          } else if (preservationMode === 'safekeeping') {
            // Safe Keeping mode: Private + IPFS sharability
            console.log('🛡️ Processing safe keeping memory (private + IPFS)...')

            // Add to Yjs array immediately (shows as "uploading")
            if (memoriesArrayRef.current) {
              memoriesArrayRef.current.push([memory])
              loadMemoriesFromStorage()
            }

            // Generate CID with Helia (if available)
            let localCid = null
            if (fsRef.current) {
              console.log('🔗 Generating CID with Helia...')
              const fileBytes = new Uint8Array(await file.arrayBuffer())
              localCid = await fsRef.current.addBytes(fileBytes)
              console.log('✅ Local CID generated:', localCid.toString())
            }

            // Upload to Pinata for permanent storage
            console.log('☁️ Uploading to Pinata...')
            const uploadResult = await uploadToPinata(file, memory)

            if (uploadResult.success) {
              updatedMemory = {
                ...memory,
                status: uploadResult.proof.pinned ? 'pinned' : 'uploaded',
                cid: uploadResult.cid,
                pinned: uploadResult.proof.pinned,
                proof: uploadResult.proof,
                localCid: localCid ? localCid.toString() : null,
                storageType: 'ipfs',
                sharable: true // Mark as sharable but private
              }
              console.log('✅ Safe keeping memory stored with IPFS sharability')
            } else {
              throw new Error(uploadResult.error || 'Upload failed')
            }
          }

          // Update in Yjs array
          if (memoriesArrayRef.current) {
            const memoriesData = memoriesArrayRef.current.toArray()
            const index = memoriesData.findIndex(m => m.id === memory.id)
            if (index >= 0) {
              memoriesArrayRef.current.delete(index, 1)
              memoriesArrayRef.current.insert(index, [updatedMemory])
            }
          }

          console.log('✅ Memory processed successfully:', updatedMemory.id)

        } catch (uploadError) {
          console.error('❌ Upload failed for file:', file.name, uploadError)

          // Update memory status to error
          if (memoriesArrayRef.current) {
            const memoriesData = memoriesArrayRef.current.toArray()
            const index = memoriesData.findIndex(m => m.id === memory.id)
            if (index >= 0) {
              const errorMemory = { ...memory, status: 'error', error: uploadError.message }
              memoriesArrayRef.current.delete(index, 1)
              memoriesArrayRef.current.insert(index, [errorMemory])
            }
          }
        }
      }

      loadMemoriesFromStorage()
      setSyncStatus('synced')

    } catch (error) {
      console.error('❌ Upload process failed:', error)
      setSyncStatus('error')
      throw error
    }
  }, [user, loadMemoriesFromStorage])

  // Helper functions
  const uploadToPinata = async (file, metadata) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('metadata', JSON.stringify(metadata))

    console.log('🔗 Using IPFS upload URL:', API_ENDPOINTS.IPFS_UPLOAD)
    console.log('📦 API_ENDPOINTS:', API_ENDPOINTS)

    const response = await fetch(API_ENDPOINTS.IPFS_UPLOAD, {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      throw new Error(`Pinata upload failed: ${response.status}`)
    }

    return await response.json()
  }

  const moderateContent = async (data) => {
    const response = await fetch(API_ENDPOINTS.AI_MODERATE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`Moderation failed: ${response.status}`)
    }

    return await response.json()
  }

  const getFileType = (file) => {
    const type = file.type.split('/')[0]
    const typeMap = {
      'image': 'image',
      'video': 'video',
      'audio': 'audio',
      'text': 'document',
      'application': 'document'
    }
    return typeMap[type] || 'document'
  }

  // Initialize when wallet is connected (with debouncing)
  useEffect(() => {
    // Clear any existing timeout
    if (walletCheckTimeoutRef.current) {
      clearTimeout(walletCheckTimeoutRef.current)
    }

    // Debounce wallet state changes
    walletCheckTimeoutRef.current = setTimeout(() => {
      const currentAddress = getWalletAddress(wallet)
      const lastAddress = lastWalletAddressRef.current

      console.log('🔍 Wallet state check:', {
        wallet: wallet,
        hasAddress: !!currentAddress,
        address: currentAddress,
        lastAddress: lastAddress,
        isInitialized,
        isInitializing: isInitializingRef.current,
        walletKeys: wallet ? Object.keys(wallet) : null,
        walletType: typeof wallet
      })

      // Only proceed if wallet address actually changed
      if (currentAddress !== lastAddress) {
        lastWalletAddressRef.current = currentAddress

        if (currentAddress) {
          // Wallet connected or changed
          console.log('🔄 Wallet connected/changed, initializing Etherith...')
          if (isInitialized) {
            console.log('🔄 Resetting for new wallet...')
            setIsInitialized(false)
            isInitializingRef.current = false
          }
          
          // Check authentication immediately when wallet is detected
          checkExistingAuth()
          
          initializeEtherith()
        } else {
          // Wallet disconnected
          console.log('🔌 Wallet disconnected, resetting state...')
          setIsInitialized(false)
          isInitializingRef.current = false
          setMemories([])
          setPublicMemories([])
          setUser(null)
          setIsAuthenticated(false)
        }
      } else {
        console.log('⏭️ Wallet state unchanged, skipping...')
      }
    }, 100) // 100ms debounce

    // Cleanup on unmount
    return () => {
      if (walletCheckTimeoutRef.current) {
        clearTimeout(walletCheckTimeoutRef.current)
      }
      if (heliaRef.current) {
        heliaRef.current.stop()
      }
      providersRef.current.forEach(provider => {
        if (provider.destroy) provider.destroy()
      })
    }
  }, [wallet?.address])

  // Check for fallback auth parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const authData = urlParams.get('auth')

    if (authData && profileDataRef.current) {
      try {
        const userData = JSON.parse(atob(authData))
        console.log('✅ Discord login from fallback URL:', userData.username)

        setUser(userData)
        setIsAuthenticated(true)

        // Store in Yjs document for persistence
        profileDataRef.current.set('user', userData)
        profileDataRef.current.set('lastLogin', Date.now())

        updateProfileUI(userData)

        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname)
      } catch (error) {
        console.error('❌ Failed to parse fallback auth data:', error)
      }
    }
  }, [updateProfileUI])

  // Enhanced peer discovery function for cross-network connectivity
  const forcePeerDiscovery = useCallback(() => {
    console.log('🔍 Forcing comprehensive peer discovery...')
    
    // Find all WebRTC providers
    const webrtcProviders = providersRef.current.filter(p => 
      p.constructor.name === 'WebrtcProvider' || p.name === 'WebrtcProvider'
    )
    
    console.log('📡 Found WebRTC providers:', webrtcProviders.length)
    
    webrtcProviders.forEach((provider, index) => {
      console.log(`🔄 Provider ${index + 1}:`, {
        roomName: provider.roomName,
        connected: provider.connected,
        connecting: provider.connecting,
        peersCount: provider.peers?.size || 0,
        signalingUrls: provider.signalingUrls
      })
      
      // Force reconnection for better peer discovery
      if (provider.disconnect && provider.connect) {
        console.log(`🔌 Disconnecting provider for room: ${provider.roomName}`)
        provider.disconnect()
        
        // Reconnect after a short delay
        setTimeout(() => {
          console.log(`🔗 Reconnecting provider for room: ${provider.roomName}`)
          provider.connect()
        }, 1000 + (index * 500)) // Stagger reconnections
      }
    })
    
    // Special focus on public archive provider
    const publicProvider = providersRef.current.find(p => 
      (p.constructor.name === 'WebrtcProvider' || p.name === 'WebrtcProvider') && 
      p.roomName === 'etherith-public-archive'
    )
    
    if (publicProvider) {
      console.log('🌐 Public provider details:', {
        roomName: publicProvider.roomName,
        connected: publicProvider.connected,
        peersCount: publicProvider.peers?.size || 0,
        maxConns: publicProvider.maxConns,
        signalingUrls: publicProvider.signalingUrls,
        bcConns: publicProvider.bcConns
      })
      
      // Force awareness update for the public provider
      if (publicProvider.awareness) {
        console.log('📢 Updating awareness state...')
        publicProvider.awareness.setLocalState({
          user: user ? { 
            name: user.username, 
            address: user.address,
            timestamp: Date.now()
          } : null,
          cursor: null,
          timestamp: Date.now()
        })
      }
    } else {
      console.warn('⚠️ Public WebRTC provider not found in providers array')
      console.log('Available providers:', providersRef.current.map(p => ({
        type: p.constructor.name || p.name,
        roomName: p.roomName,
        docName: p.docName
      })))
    }
    
    // Update debug info after a delay to capture reconnection status
    setTimeout(() => {
      updateDebugInfo()
      console.log('✅ Peer discovery completed, debug info updated')
    }, 3000)
    
  }, [updateDebugInfo, user])

  // Comprehensive network diagnostic function based on Yjs documentation
  const checkSyncStatus = useCallback(() => {
    console.log('🔄 Comprehensive sync status check...')
    
    const diagnostic = {
      // Basic state
      timestamp: new Date().toISOString(),
      networkStatus,
      peerCount,
      connectionStatus,
      syncStatus,
      
      // Document states
      documents: {
        userDoc: ydocRef.current ? {
          clientID: ydocRef.current.clientID,
          gc: ydocRef.current.gc,
          isDestroyed: ydocRef.current.isDestroyed || false
        } : null,
        publicDoc: publicYdocRef.current ? {
          clientID: publicYdocRef.current.clientID,
          gc: publicYdocRef.current.gc,
          isDestroyed: publicYdocRef.current.isDestroyed || false
        } : null
      },
      
      // Storage arrays
      storageArrays: {
        memoriesArray: {
          exists: !!memoriesArrayRef.current,
          length: memoriesArrayRef.current?.length || 0
        },
        profileData: {
          exists: !!profileDataRef.current
        },
        archiveData: {
          exists: !!archiveDataRef.current,
          length: archiveDataRef.current?.length || 0
        }
      },
      
      // Provider states with detailed peer information
      providers: providersRef.current.map((provider, index) => {
        const peerDetails = provider.peers ? Array.from(provider.peers.entries()).map(([peerId, peer]) => ({
          peerId: peerId.slice(0, 8) + '...',
          connectionState: peer.connectionState,
          iceConnectionState: peer.iceConnectionState,
          iceGatheringState: peer.iceGatheringState,
          signalingState: peer.signalingState
        })) : []
        
        return {
          index: index,
          type: provider.constructor.name || provider.name || 'unknown',
          roomName: provider.roomName || provider.docName,
          connected: provider.connected,
          connecting: provider.connecting,
          synced: provider.synced,
          awareness: !!provider.awareness,
          peersCount: provider.peers?.size || 0,
          signalingUrls: provider.signalingUrls || [],
          maxConns: provider.maxConns,
          filterBcConns: provider.filterBcConns,
          bcConns: provider.bcConns,
          peerDetails: peerDetails
        }
      }),
      
      // WebRTC specific diagnostics
      webrtcDiagnostics: {
        signalingServers: [
          'wss://y-webrtc-signaling-eu.herokuapp.com/',
          'wss://signaling.yjs.dev/'
        ],
        connections: providersRef.current
          .filter(p => p.constructor.name === 'WebrtcProvider')
          .map(p => ({
            roomName: p.roomName,
            connected: p.connected,
            connecting: p.connecting,
            peersCount: p.peers?.size || 0,
            signalingUrls: p.signalingUrls,
            peerDetails: p.peers ? Array.from(p.peers.entries()).map(([peerId, peer]) => ({
              peerId: peerId,
              connectionState: peer.connectionState,
              iceConnectionState: peer.iceConnectionState
            })) : []
          }))
      },
      
      // IndexedDB specific diagnostics
      indexedDBDiagnostics: {
        connections: providersRef.current
          .filter(p => p.constructor.name === 'IndexeddbPersistence')
          .map(p => ({
            docName: p.docName,
            synced: p.synced
          }))
      },
      
      // Memory counts
      memoryCounts: {
        userMemories: memoriesArrayRef.current?.length || 0,
        publicMemories: archiveDataRef.current?.length || 0,
        displayedMemories: memories.length,
        displayedPublicMemories: publicMemories.length
      },
      
      // Cross-network connectivity assessment
      crossNetworkStatus: {
        hasPublicPeers: providersRef.current
          .find(p => p.constructor.name === 'WebrtcProvider' && p.roomName === 'etherith-public-archive')
          ?.peers?.size > 0 || false,
        publicProviderConnected: providersRef.current
          .find(p => p.constructor.name === 'WebrtcProvider' && p.roomName === 'etherith-public-archive')
          ?.connected || false,
        lastPeerCheck: new Date().toISOString()
      }
    }
    
    console.log('📊 Comprehensive diagnostic:', diagnostic)
    
    // Force reload both memories and public archive
    loadMemoriesFromStorage()
    loadPublicArchive()
    updateDebugInfo()
    
    return diagnostic
  }, [loadMemoriesFromStorage, loadPublicArchive, updateDebugInfo, networkStatus, peerCount, connectionStatus, syncStatus, memories.length, publicMemories.length])

  return {
    isInitialized,
    user,
    memories,
    publicMemories,
    connectionStatus,
    syncStatus,
    debugInfo,
    isAuthenticated,
    uploadMemory,
    loadMemories: loadMemoriesFromStorage,
    loadPublicArchive,
    searchArchive,
    checkSyncStatus,
    forcePeerDiscovery,
    peerCount,
    networkStatus,
    isWalletConnected: !!getWalletAddress(wallet),
    initializeStorageArrays
  }
}